import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Flex, Grid, Heading, Text, Select, Input, InputGroup, InputLeftElement, Icon, Spinner, Checkbox, Button,
  Alert, AlertIcon, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
} from '@chakra-ui/react';
import { FiSearch, FiUpload } from 'react-icons/fi';
import useAuth from 'hooks/useAuth';
import { STUDENT_SCHOOL_GROUPS } from 'utils/studentSchools';
import {
  getAssignmentOptions, getAssignmentStudents, applySchool, ROLES, STATUS_LABEL, schoolLabel,
  expectedSchoolGroup, groupOfSchool, errorMessage,
} from 'api/administrador/schoolAssignment';
import { StatusPill } from 'components/monitoring/ai/common';
import AiPagination from 'components/monitoring/ai/AiPagination';
import ImportExcel from './ImportExcel';

/**
 * Asignar colegio en bloque: filtra estudiantes por su matrícula y les asigna el colegio de una vez.
 * El colegio es la institución que certifica sus documentos (lo usa la Ventanilla Klazia).
 */

const PAGE_SIZE = 50;
const COLS = '36px 2fr 1.6fr 1.2fr 2.4fr';
const INITIAL = { programType: '', grade: '', technicalCareer: '', semester: '', entryFrom: '', entryTo: '', status: 'activos', school: 'sin', search: '', sort: 'codigo', page: 1 };

export default function AsignarColegio() {
  const { user } = useAuth();
  const isAllowed = ROLES.includes(user?.role?.type);
  const toast = useToast();

  const [options, setOptions] = useState(null);
  const [filters, setFilters] = useState(INITIAL);
  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [bySchool, setBySchool] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [target, setTarget] = useState('');
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  // Colegio actual de cada estudiante visto, para decir en la confirmación cuántos cambian de verdad.
  const schoolOf = useRef({});

  useEffect(() => {
    if (!isAllowed) return;
    getAssignmentOptions().then(setOptions).catch(() => setOptions(null));
  }, [isAllowed]);

  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchText ? f : { ...f, search: searchText, page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchText]);

  const base = useMemo(() => ({
    programType: filters.programType,
    grade: filters.grade,
    technicalCareer: filters.technicalCareer,
    semester: filters.semester,
    entryFrom: filters.entryFrom,
    entryTo: filters.entryTo,
    status: filters.status,
    school: filters.school,
    search: filters.search,
  }), [filters.programType, filters.grade, filters.technicalCareer, filters.semester, filters.entryFrom, filters.entryTo, filters.status, filters.school, filters.search]);

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      const res = await getAssignmentStudents({ ...base, sort: filters.sort, page: filters.page, pageSize: PAGE_SIZE });
      (res?.data || []).forEach((r) => { schoolOf.current[r.id] = r.school || null; });
      setRows(res?.data || []);
      setPagination(res?.pagination || null);
      setBySchool(res?.bySchool || []);
      setError('');
    } catch (e) {
      // Sin lista: así no queda a la vista (ni para «Asignar a todos») el resultado de un filtro anterior.
      setRows([]);
      setPagination(null);
      setBySchool([]);
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [base, filters.sort, filters.page]);

  useEffect(() => { if (isAllowed) load(false); }, [isAllowed, load]);
  useEffect(() => { setSelected(new Set()); }, [base]);
  // Si se filtra por un colegio es para cambiarlo: «Solo a quienes no tienen colegio» no aplicaría a nadie.
  useEffect(() => { setOnlyEmpty(filters.school === 'sin'); }, [filters.school]);

  const programType = (options?.programTypes || []).find((p) => String(p.id) === String(filters.programType));
  const grades = (options?.grades || []).filter((g) => !programType || String(g.programTypeId) === String(programType.id));
  const careers = (options?.technicalCareers || []).filter((c) => !programType || String(c.programTypeId) === String(programType.id));
  const isTechnical = programType?.type === 'technical_career';
  const total = pagination?.total || 0;

  const set = (name, extra = {}) => (e) => setFilters((f) => ({ ...f, [name]: e.target.value, page: 1, ...extra }));
  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allOnPage) rows.forEach((r) => next.delete(r.id)); else rows.forEach((r) => next.add(r.id));
    return next;
  });

  const expected = expectedSchoolGroup(programType?.name);
  const mismatch = target && expected && groupOfSchool(target) && groupOfSchool(target) !== expected;

  const ask = (mode) => {
    if (!target) {
      setError('Elige el colegio que quieres asignar.');
      return;
    }
    setError('');
    // Cuántos cambian: sin colegio + (si se permite) los que tienen otro colegio.
    let empty = 0;
    let other = 0;
    if (mode === 'selected') {
      [...selected].forEach((id) => {
        const cur = schoolOf.current[id];
        if (!cur) empty += 1; else if (cur !== target) other += 1;
      });
    } else if (filters.school === 'sin') {
      empty = total;
    } else if (filters.school !== 'todos') {
      other = filters.school === target ? 0 : total;
    } else {
      // El reparto por colegio ignora el filtro de colegio: solo coincide con el total cuando es «Con o sin colegio».
      bySchool.forEach((b) => {
        if (!b.school) empty += Number(b.total); else if (b.school !== target) other += Number(b.total);
      });
    }
    setConfirm({ mode, count: mode === 'selected' ? selected.size : total, empty, other });
  };

  const run = async () => {
    setBusy(true);
    try {
      const body = confirm.mode === 'selected'
        ? { school: target, onlyEmpty, studentIds: [...selected] }
        : { school: target, onlyEmpty, filters: base, expectedTotal: total };
      const res = await applySchool(body);
      toast({
        status: 'success',
        title: `${res.updated.toLocaleString('es-CO')} ${res.updated === 1 ? 'estudiante quedó' : 'estudiantes quedaron'} en ${schoolLabel(res.school)}`,
        description: res.skipped ? `${res.skipped.toLocaleString('es-CO')} no se cambiaron porque ya tenían colegio o ya era ese.` : undefined,
        duration: 6000,
        isClosable: true,
      });
      setConfirm(null);
      setSelected(new Set());
      load(true);
    } catch (e) {
      setError(errorMessage(e));
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  if (!isAllowed) {
    return <Box p={8}><Text color="gray.500">Esta vista está disponible solo para el área administrativa.</Text></Box>;
  }

  return (
    <Box p={{ base: 3, md: 6 }}>
      <Flex mb={5} gap={3} align="flex-start" justify="space-between" wrap="wrap">
        <Box>
          <Heading fontSize="22px" fontWeight="700" letterSpacing="-.01em">Asignar colegio</Heading>
          <Text color="gray.500" fontSize="14px" mt={1} maxW="760px">
            Filtra estudiantes por su matrícula y asígnales el colegio de una vez. El colegio es la institución que certifica sus
            constancias y certificados.
          </Text>
        </Box>
        <Button leftIcon={<Icon as={FiUpload} />} variant="outline" borderRadius="8px" onClick={() => setImportOpen(true)}>
          Importar desde Excel
        </Button>
      </Flex>

      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }} gap={3} mb={4}>
        <Select aria-label="Programa" bg="white" borderRadius="8px" value={filters.programType} onChange={set('programType', { grade: '', technicalCareer: '' })}>
          <option value="">Todos los programas</option>
          {(options?.programTypes || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        {isTechnical ? (
          <Select aria-label="Técnica" bg="white" borderRadius="8px" value={filters.technicalCareer} onChange={set('technicalCareer')}>
            <option value="">Todas las técnicas</option>
            {careers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        ) : (
          <Select aria-label="Grado" bg="white" borderRadius="8px" value={filters.grade} onChange={set('grade')} isDisabled={!programType}>
            <option value="">{programType ? 'Todos los grados' : 'Elige un programa para ver grados'}</option>
            {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </Select>
        )}
        <Select aria-label="Semestre" bg="white" borderRadius="8px" value={filters.semester} onChange={set('semester')}>
          <option value="">Todos los semestres</option>
          {(options?.semesters || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
        {/* Fecha de ingreso = cuándo se creó la matrícula (para la asignación masiva de inicio de año). */}
        <Flex align="center" gap={2}>
          <Text as="label" htmlFor="ac-entry-from" fontSize="13px" color="gray.600" whiteSpace="nowrap">Ingreso desde</Text>
          <Input id="ac-entry-from" type="date" bg="white" borderRadius="8px" value={filters.entryFrom} max={filters.entryTo || undefined} onChange={set('entryFrom')} />
        </Flex>
        <Flex align="center" gap={2}>
          <Text as="label" htmlFor="ac-entry-to" fontSize="13px" color="gray.600" whiteSpace="nowrap">hasta</Text>
          <Input id="ac-entry-to" type="date" bg="white" borderRadius="8px" value={filters.entryTo} min={filters.entryFrom || undefined} onChange={set('entryTo')} />
        </Flex>
        <Select aria-label="Estado de la matrícula" bg="white" borderRadius="8px" value={filters.status} onChange={set('status')}>
          <option value="activos">Activos (Estudiando, Promoción Pendiente o Registrado)</option>
          {(options?.statuses || Object.keys(STATUS_LABEL)).map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
          <option value="todos">Cualquier estado</option>
        </Select>
        <Select aria-label="Colegio actual" bg="white" borderRadius="8px" value={filters.school} onChange={set('school')}>
          <option value="sin">Sin colegio</option>
          <option value="todos">Con o sin colegio</option>
          {STUDENT_SCHOOL_GROUPS.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </optgroup>
          ))}
        </Select>
        <Select aria-label="Ordenar por" bg="white" borderRadius="8px" value={filters.sort} onChange={set('sort')}>
          <option value="codigo">Ordenar por código</option>
          <option value="apellido">Ordenar por apellido</option>
        </Select>
        <InputGroup>
          <InputLeftElement pointerEvents="none"><Icon as={FiSearch} color="gray.400" /></InputLeftElement>
          <Input
            aria-label="Buscar por nombre, documento, correo o código" placeholder="Nombre, documento o código"
            bg="white" borderRadius="8px" value={searchText} onChange={(e) => setSearchText(e.target.value)}
          />
        </InputGroup>
      </Grid>

      {bySchool.length > 0 && (
        <Flex gap={2} wrap="wrap" align="center" mb={4}>
          <Text fontSize="12px" color="gray.500" mr={1}>Con este filtro, por colegio:</Text>
          {bySchool.map((b) => (
            <StatusPill key={b.school || 'sin'} label={`${schoolLabel(b.school)} · ${Number(b.total).toLocaleString('es-CO')}`} tone={b.school ? 'gray' : 'orange'} />
          ))}
        </Flex>
      )}

      {error && (
        <Alert status="error" borderRadius="8px" mb={4} fontSize="sm">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px">
        <Flex px={5} py={4} gap={3} align="center" wrap="wrap" borderBottom="1px solid" borderColor="gray.100">
          <Text fontSize="sm" fontWeight="600" mr={2}>
            {loading ? 'Buscando…' : `${total.toLocaleString('es-CO')} ${total === 1 ? 'estudiante' : 'estudiantes'}`}
            {selected.size > 0 && <Text as="span" color="purple.600"> · {selected.size} seleccionados</Text>}
          </Text>
          <Select
            aria-label="Colegio a asignar" w={{ base: '100%', md: '300px' }} size="sm" borderRadius="8px"
            value={target} onChange={(e) => setTarget(e.target.value)}
          >
            <option value="">Colegio a asignar…</option>
            {STUDENT_SCHOOL_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </optgroup>
            ))}
          </Select>
          <Checkbox colorScheme="purple" isChecked={onlyEmpty} onChange={(e) => setOnlyEmpty(e.target.checked)}>
            <Text fontSize="sm" color="gray.600">Solo a quienes no tienen colegio</Text>
          </Checkbox>
          <Flex gap={2} ml={{ base: 0, md: 'auto' }} wrap="wrap">
            <Button size="sm" variant="outline" onClick={() => ask('selected')} isDisabled={!selected.size}>
              Asignar a seleccionados ({selected.size})
            </Button>
            <Button size="sm" bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={() => ask('filter')} isDisabled={!total}>
              Asignar a todos ({total.toLocaleString('es-CO')})
            </Button>
          </Flex>
        </Flex>

        <Box overflowX="auto">
          <Box minW="900px">
            <Grid templateColumns={COLS} px={5} py={3} gap={3} bg="gray.50" fontSize="11px" fontWeight="600" letterSpacing=".06em" color="gray.600" textTransform="uppercase">
              <Checkbox
                aria-label="Seleccionar todos los de esta página" colorScheme="purple" isChecked={allOnPage}
                isIndeterminate={!allOnPage && rows.some((r) => selected.has(r.id))} onChange={toggleAll}
              />
              <Text>Estudiante</Text><Text>Correo</Text><Text>Colegio actual</Text><Text>Matrícula</Text>
            </Grid>
            {loading && rows.length === 0 && <Flex justify="center" py={10}><Spinner color="#574feb" /></Flex>}
            {!loading && rows.length === 0 && (
              <Text textAlign="center" py={10} color="gray.500" fontSize="sm">No hay estudiantes con esos filtros.</Text>
            )}
            {rows.map((r) => (
              <Grid
                key={r.id} templateColumns={COLS} px={5} py={3} gap={3} alignItems="center" borderTop="1px solid" borderColor="gray.100"
                bg={selected.has(r.id) ? 'purple.50' : undefined} cursor="pointer" onClick={() => toggle(r.id)}
              >
                <Box onClick={(e) => e.stopPropagation()}>
                  <Checkbox aria-label={`Seleccionar a ${r.name || r.id}`} colorScheme="purple" isChecked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                </Box>
                <Box minW={0}>
                  <Text fontSize="sm" fontWeight="600" isTruncated>{r.name || 'Sin nombre'}</Text>
                  <Text fontSize="xs" color="gray.500" fontFamily="mono">Código {r.id}{r.identity ? ` · ${r.identity}` : ''}</Text>
                </Box>
                <Text fontSize="sm" color="gray.600" isTruncated>{r.email || '—'}</Text>
                <Text fontSize="sm" color={r.school ? 'gray.700' : 'orange.500'} fontWeight={r.school ? 400 : 600}>{schoolLabel(r.school)}</Text>
                <Box minW={0}>
                  {r.programs.slice(0, 2).map((p) => <Text key={p} fontSize="12px" color="gray.600" isTruncated>{p}</Text>)}
                  {r.programs.length > 2 && <Text fontSize="11px" color="gray.400">+{r.programs.length - 2} más</Text>}
                </Box>
              </Grid>
            ))}
          </Box>
        </Box>

        <AiPagination pagination={pagination} noun="estudiantes" onPageChange={(page) => setFilters((f) => ({ ...f, page }))} />
      </Box>

      <ImportExcel isOpen={importOpen} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(true); }} />

      <Modal isOpen={!!confirm} onClose={busy ? () => {} : () => setConfirm(null)} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader fontSize="18px">Asignar {schoolLabel(target)}</ModalHeader>
          <ModalBody>
            {confirm && (
              <Text fontSize="sm">
                Se asignará <b>{schoolLabel(target)}</b> a{' '}
                <b>{confirm.count.toLocaleString('es-CO')} {confirm.count === 1 ? 'estudiante' : 'estudiantes'}</b>
                {confirm.mode === 'filter' ? ' (todos los que cumplen el filtro, en todas las páginas)' : ' seleccionados'}.
                {onlyEmpty
                  ? ' Solo cambian quienes no tienen colegio; los que ya tienen uno se quedan como están.'
                  : ' También se cambia el colegio de quienes ya tenían uno.'}
              </Text>
            )}
            {confirm && (
              <Text fontSize="sm" mt={2}>
                Cambian <b>{(confirm.empty + (onlyEmpty ? 0 : confirm.other)).toLocaleString('es-CO')}</b>:{' '}
                {confirm.empty.toLocaleString('es-CO')} sin colegio
                {onlyEmpty
                  ? (confirm.other ? ` (otros ${confirm.other.toLocaleString('es-CO')} tienen otro colegio y no se tocan)` : '')
                  : ` y ${confirm.other.toLocaleString('es-CO')} que tienen otro colegio`}.
              </Text>
            )}
            {mismatch && (
              <Alert status="warning" borderRadius="8px" mt={3} fontSize="sm">
                <AlertIcon />
                Este colegio es de «{groupOfSchool(target)}» y el filtro es de un programa de «{expected}». Revisa antes de aplicar.
              </Alert>
            )}
            <Text fontSize="12px" color="gray.500" mt={3}>Cada cambio queda en el historial del estudiante.</Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={() => setConfirm(null)} isDisabled={busy}>Cancelar</Button>
            <Button bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={run} isLoading={busy}>Asignar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
